import {
  useTradeAccountsStore,
  TradeAccount,
} from "@/lib/stores/userTradeAccounts";
import { usePositionsStore, Position } from "@/lib/stores/positions";
import { useAccountInfoStore } from "@/lib/stores/accountInfoStore";
import { X } from "lucide-react";
import { useBinanceAccount } from "@/hooks/exchanges/useBinanceAccount";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ClosePositionDialogProps {
  open: boolean;
  position: Position | null;
  account: TradeAccount | null;
  onClose: () => void;
}

export function ClosePositionDialog({
  open,
  position,
  account,
  onClose,
}: ClosePositionDialogProps) {
  const { closePosition, refetch } = useBinanceAccount(account);
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closeSuccess, setCloseSuccess] = useState<string | null>(null);

  const onConfirmClose = async () => {
    if (!position || !account) {
      return;
    }
    setCloseLoading(true);
    setCloseError(null);
    setCloseSuccess(null);
    try {
      await closePosition(position);
      setCloseSuccess("Position closed successfully.");
      await refetch();
      setTimeout(() => {
        setCloseSuccess(null);
        onClose();
      }, 1000);
    } catch (err: any) {
      setCloseError(err.message || "Failed to close position");
    } finally {
      setCloseLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close Position</DialogTitle>
        </DialogHeader>
        <div className="mb-2">
          Are you sure you want to close this position?
          <div className="mt-2 text-xs">
            <strong>Symbol:</strong> {position?.symbol}
            <br />
            <strong>Side:</strong> {position?.side}
            <br />
            <strong>Size:</strong> {position?.size}
            <br />
            <strong>Entry Price:</strong> {position?.entryPrice}
            <br />
          </div>
        </div>
        {closeError && (
          <div className="text-red-500 text-xs mb-2">{closeError}</div>
        )}
        {closeSuccess && (
          <div className="text-green-500 text-xs mb-2">{closeSuccess}</div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={closeLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirmClose} disabled={closeLoading}>
            {closeLoading ? "Closing..." : "Close Position"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const PositionsList = ({ symbol }: { symbol?: string }) => {
  const { positions, isLoading, hasError } = usePositionsStore();
  const { accounts } = useTradeAccountsStore();
  const { getAccountInfo } = useAccountInfoStore();

  // State for close confirmation dialog
  const [closeDialog, setCloseDialog] = useState<{
    open: boolean;
    position: Position | null;
    account: TradeAccount | null;
  }>({ open: false, position: null, account: null });

  // Handler for clicking the close icon
  const onClosePositionClick = (position: Position, account: TradeAccount) => {
    setCloseDialog({ open: true, position, account });
  };

  // Debug logs
  // console.log("ACCOUNTS", accounts);
  // console.log("POSITIONS STORE", positions);
  // accounts.forEach(account => {
  //   console.log(
  //     `Account: ${account.id} (${account.label})`,
  //     positions[account.id]
  //   );
  // });
  // console.log("SYMBOL PROP", symbol);

  return (
    <div className="mt-4">
      <h4 className="font-semibold mb-2">Open Positions by Account</h4>
      <ClosePositionDialog
        open={closeDialog.open}
        position={closeDialog.position}
        account={closeDialog.account}
        onClose={() =>
          setCloseDialog({ open: false, position: null, account: null })
        }
      />
      {accounts.map((account) => {
        const accountPositions = positions[account.id] || [];
        const loading = isLoading[account.id] || false;
        const error = hasError[account.id] || null;

        // Temporarily remove symbol filter for debugging
        // const filteredPositions = symbol
        //   ? accountPositions.filter(
        //       (pos) => pos.symbol.toLowerCase() === symbol.toLowerCase()
        //     )
        //   : accountPositions;
        const filteredPositions = accountPositions;

        // Extra info
        const totalPnL = filteredPositions.reduce(
          (sum, pos) => sum + (pos.pnl || 0),
          0,
        );
        const openCount = filteredPositions.length;
        const accountInfo = getAccountInfo(account.id);
        const balance = accountInfo?.availableBalance;

        if (loading) {
          return (
            <div key={account.id} className="mb-4">
              <div className="font-semibold">{account.label || account.id}</div>
              <div>Loading positions...</div>
            </div>
          );
        }
        if (error) {
          return (
            <div key={account.id} className="mb-4">
              <div className="font-semibold">{account.label || account.id}</div>
              <div className="text-red-500">
                Error loading positions: {error}
              </div>
            </div>
          );
        }
        if (!filteredPositions.length) {
          return null;
        }

        return (
          <div key={account.id} className="mb-4">
            <div className="font-semibold mb-1">
              {account.label || account.id}{" "}
              <span className="text-xs text-muted-foreground">
                ({account.exchange} [{account.exchangeType}])
              </span>
            </div>
            <div className="mb-2 text-xs">
              Open Positions: {openCount} | Total PnL:{" "}
              <span
                className={totalPnL >= 0 ? "text-green-500" : "text-red-500"}
              >
                {totalPnL.toFixed(2)}
              </span>
              {balance !== undefined && (
                <> | Balance: ${Number(balance).toFixed(2)}</>
              )}
            </div>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-neutral-700">
                  <th className="text-left py-1">Symbol</th>
                  <th className="text-left py-1">Side</th>
                  <th className="text-left py-1">Size</th>
                  <th className="text-left py-1">Entry Price</th>
                  <th className="text-left py-1">PnL</th>
                  <th className="text-left py-1"></th>
                </tr>
              </thead>
              <tbody>
                {filteredPositions.map((pos) => (
                  <tr
                    key={pos.id + pos.symbol}
                    className="border-b border-neutral-800"
                  >
                    <td className="py-1">{pos.symbol}</td>
                    <td
                      className={`py-1 ${pos.side === "LONG" ? "text-green-500" : "text-red-500"}`}
                    >
                      {pos.side}
                    </td>
                    <td className="py-1">{pos.size}</td>
                    <td className="py-1">{pos.entryPrice}</td>
                    <td
                      className={
                        pos.pnl >= 0 ? "text-green-500" : "text-red-500"
                      }
                    >
                      {pos.pnl}
                    </td>
                    <td className="py-1">
                      <button
                        className="text-red-500 hover:text-red-700"
                        title="Close Position"
                        onClick={() => onClosePositionClick(pos, account)}
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};
